import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
} from 'firebase/firestore'

import { firestore, httpsCallable } from './app'

export const NOTIFICATIONS_PAGE_SIZE = 10

const LEGACY_READ_IDS_KEY = 'legacy_read_notification_ids'

export function getLegacyReadIds() {
  try {
    const stored = localStorage.getItem(LEGACY_READ_IDS_KEY)
    return Array.isArray(JSON.parse(stored)) ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export function saveLegacyNotificationRead(notificationId) {
  try {
    const readIds = getLegacyReadIds()
    if (!readIds.includes(notificationId)) {
      const updated = [...readIds, notificationId]
      localStorage.setItem(LEGACY_READ_IDS_KEY, JSON.stringify(updated))
      // Dispatch storage event so same-tab listeners (e.g. bell) can react
      window.dispatchEvent(new StorageEvent('storage', { key: LEGACY_READ_IDS_KEY }))
    }
  } catch {
    // localStorage not available
  }
}

function isMissingIndexError(error) {
  return error?.code === 'failed-precondition' && /requires an index/i.test(error?.message || '')
}

function getNotificationsCollection(userId) {
  return collection(firestore, 'users', String(userId), 'notifications')
}

export async function fetchNotificationUserContext(userId) {
  if (!userId) {
    return { employerId: null, calculationId: null }
  }

  const userSnapshot = await getDoc(doc(firestore, 'users', String(userId)))

  if (!userSnapshot.exists()) {
    return { employerId: null, calculationId: null }
  }

  const userData = userSnapshot.data() || {}

  return {
    employerId: userData.employerId ?? null,
    calculationId: userData.calculationId ?? null,
  }
}

function mapNotificationDoc(notificationDoc) {
  return {
    id: notificationDoc.id,
    ...notificationDoc.data(),
  }
}

function isValidNotification(n) {
  return Boolean(n?.subject || n?.body || n?.type)
}

export function compareNotifications(left, right) {
  if (Boolean(left?.isRead) !== Boolean(right?.isRead)) {
    return Number(Boolean(left?.isRead)) - Number(Boolean(right?.isRead))
  }

  const leftMillis = typeof left?.createdAt?.toDate === 'function'
    ? left.createdAt.toDate().getTime()
    : new Date(left?.createdAt || 0).getTime()
  const rightMillis = typeof right?.createdAt?.toDate === 'function'
    ? right.createdAt.toDate().getTime()
    : new Date(right?.createdAt || 0).getTime()

  return rightMillis - leftMillis
}

function buildLegacyNotificationDate(dayRecord, timesheetDoc) {
  return (
    dayRecord?.rejectedAt ||
    dayRecord?.notificationDeliveryAttemptedAt ||
    dayRecord?.updatedAt ||
    timesheetDoc?.updatedAt ||
    new Date(
      Number(dayRecord?.year ?? timesheetDoc?.year ?? 0),
      Number(dayRecord?.month ?? timesheetDoc?.month ?? 1) - 1,
      Number(dayRecord?.day ?? 1),
      12,
      0,
      0,
      0
    ).toISOString()
  )
}

function extractRejectionReason(dayRecord) {
  const candidates = [
    dayRecord?.rejectionReason,
    dayRecord?.rejectionNotes,
    dayRecord?.managerRejectionReason,
    dayRecord?.sickDayDetails?.rejectionReason,
    dayRecord?.sickDayDetails?.rejectionNotes,
    dayRecord?.originalSickDayDetails?.rejectionReason,
    dayRecord?.originalSickDayDetails?.rejectionNotes,
  ]

  for (const candidate of candidates) {
    const text = String(candidate || '').trim()
    if (text) {
      return text
    }
  }

  return ''
}

function mapLegacyRejectedSickDay(dayRecord, timesheetDoc) {
  const year = Number(dayRecord?.year ?? timesheetDoc?.year ?? 0)
  const month = Number(dayRecord?.month ?? timesheetDoc?.month ?? 0)
  const day = Number(dayRecord?.day ?? 0)
  const reason = extractRejectionReason(dayRecord)
  const formattedDate = [day, month, year].map((v) => String(v).padStart(2, '0')).join('/')
  const bodyContent = reason
    ? `בקשת יום המחלה שלך עבור ${formattedDate} נדחתה.\n\nסיבת הדחייה: ${reason}`
    : `בקשת יום המחלה שלך עבור ${formattedDate} נדחתה.`

  return {
    id: `legacy-rejected-sick-day-${year}-${month}-${day}`,
    subject: 'בקשת יום מחלה נדחתה',
    body: bodyContent,
    isRead: false,
    createdAt: buildLegacyNotificationDate(dayRecord, timesheetDoc),
    category: 'attendance',
    type: 'sickDayRejected',
    metadata: {
      employerId: Number(dayRecord?.employerId ?? 0) || undefined,
      calculationId: Number(dayRecord?.calculationId ?? 0) || undefined,
      year,
      month,
      day,
      rejectionReason: reason || undefined,
      legacy: true,
    },
  }
}

export async function fetchLegacyRejectedSickDayNotifications(employerId, calculationId) {
  if (!employerId || !calculationId) {
    return []
  }

  const timesheetsRef = collection(
    firestore,
    'employers',
    String(employerId),
    'calculations',
    String(calculationId),
    'timesheets'
  )

  const snapshot = await getDocs(timesheetsRef)

  return snapshot.docs
    .map((timesheetDoc) => ({ id: timesheetDoc.id, ...timesheetDoc.data() }))
    .flatMap((timesheetDoc) => {
      const days = Array.isArray(timesheetDoc?.days) ? timesheetDoc.days : []

          return days
            .filter((dayRecord) => {
              const isRejected = dayRecord?.editStatus === 'rejected'
              const hasSickDayMarkers =
                dayRecord?.leave === 'sick' ||
                dayRecord?.originalLeave === 'sick' ||
                Boolean(dayRecord?.sickDayDetails) ||
                Boolean(dayRecord?.originalSickDayDetails)
              const hasNotificationFields =
                Boolean(dayRecord?.notificationDeliveryStatus) ||
                Boolean(dayRecord?.notificationId) ||
                Boolean(dayRecord?.notificationRecipientUserId)
              const hasRejectionReason = Boolean(String(dayRecord?.rejectionReason || '').trim())
              const isSickDay = hasSickDayMarkers || hasNotificationFields || hasRejectionReason

              // Show ALL rejected sick-days regardless of whether a reason was stored
              return isRejected && isSickDay
            })
        .map((dayRecord) => mapLegacyRejectedSickDay(dayRecord, timesheetDoc))
    })
    .sort(compareNotifications)
}

export async function fetchScopedSickDayNotifications(employerId, calculationId) {
  if (!employerId || !calculationId) {
    return []
  }

  const getScopedNotifications = httpsCallable('getMyScopedSickDayNotifications')
  const result = await getScopedNotifications({
    employerId: Number(employerId),
    calculationId: Number(calculationId),
  })

  return Array.isArray(result?.data?.notifications)
    ? result.data.notifications.sort(compareNotifications)
    : []
}

export function buildNotificationsPageQuery(userId, afterDoc = null, pageSize = NOTIFICATIONS_PAGE_SIZE) {
  const constraints = [
    orderBy('isRead', 'asc'),
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  ]

  if (afterDoc) {
    constraints.splice(2, 0, startAfter(afterDoc))
  }

  return query(getNotificationsCollection(userId), ...constraints)
}

function buildNotificationsFallbackQuery(userId, afterDoc = null, pageSize = NOTIFICATIONS_PAGE_SIZE) {
  const constraints = [
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  ]

  if (afterDoc) {
    constraints.splice(1, 0, startAfter(afterDoc))
  }

  return query(getNotificationsCollection(userId), ...constraints)
}

export async function fetchNotificationsPage(userId, afterDoc = null, pageSize = NOTIFICATIONS_PAGE_SIZE) {
  let snapshot

  try {
    snapshot = await getDocs(buildNotificationsPageQuery(userId, afterDoc, pageSize))
  } catch (error) {
    if (!isMissingIndexError(error)) {
      throw error
    }

    console.warn('Notifications query fallback applied because the composite index is missing.')
    snapshot = await getDocs(buildNotificationsFallbackQuery(userId, afterDoc, pageSize))
  }

  const docs = snapshot.docs || []
  const notifications = docs.map(mapNotificationDoc).filter(isValidNotification).sort(compareNotifications)

  return {
    notifications,
    lastDoc: docs.length ? docs[docs.length - 1] : null,
  }
}

export async function fetchNotificationsPageLoose(userId, page = 1, pageSize = NOTIFICATIONS_PAGE_SIZE) {
  const snapshot = await getDocs(getNotificationsCollection(userId))
  const notifications = (snapshot.docs || [])
    .map(mapNotificationDoc)
    .filter(isValidNotification)
    .sort(compareNotifications)

  const startIndex = Math.max(0, (page - 1) * pageSize)
  return {
    notifications: notifications.slice(startIndex, startIndex + pageSize),
    totalCount: notifications.length,
  }
}

export async function fetchNotificationsCount(userId) {
  const snapshot = await getDocs(getNotificationsCollection(userId))
  return (snapshot.docs || []).filter((d) => isValidNotification({ id: d.id, ...d.data() })).length
}

export function subscribeToUnreadNotifications(userId, onNotifications, onError) {
  return onSnapshot(
    getNotificationsCollection(userId),
    (snapshot) => {
      const notifications = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter(isValidNotification)

      onNotifications(notifications)
    },
    onError
  )
}

export function subscribeToUnreadNotificationsCount(userId, onCount, onError) {
  return subscribeToUnreadNotifications(
    userId,
    (notifications) => {
      const count = notifications.filter((n) => n?.isRead !== true).length
      onCount(count)
    },
    onError
  )
}

export async function markNotificationAsRead(userId, notificationId) {
  return updateDoc(
    doc(firestore, 'users', String(userId), 'notifications', String(notificationId)),
    {
      isRead: true,
      readAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
  )
}

export async function fetchNotificationsFallbackFromServer() {
  const getFallback = httpsCallable('getMyNotificationsFallback')
  const result = await getFallback({})

  return Array.isArray(result?.data?.notifications)
    ? result.data.notifications.sort(compareNotifications)
    : []
}

export async function seedDemoNotifications(count = 12) {
  const seedNotifications = httpsCallable('seedMyNotifications')
  const result = await seedNotifications({ count })
  return result?.data || { success: false, seededCount: 0 }
}